import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, Search, Bus } from 'lucide-react';
import { toast } from 'sonner';

export const TiposViajePage = () => {
  const [tipos, setTipos] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentTipo, setCurrentTipo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ nombre: '' });

  const filteredTipos = useMemo(() => {
    if (!searchTerm.trim()) return tipos;
    const term = searchTerm.toLowerCase();
    return tipos.filter(t => t.nombre?.toLowerCase().includes(term));
  }, [tipos, searchTerm]);

  useEffect(() => {
    loadTipos();
  }, []);

  const loadTipos = async () => {
    try {
      const response = await api.get('/tipos-viaje');
      setTipos(response.data);
    } catch (error) {
      toast.error('Error al cargar tipos de viaje');
    }
  };

  const handleOpenDialog = (tipo = null) => {
    if (tipo) {
      setCurrentTipo(tipo);
      setFormData({ nombre: tipo.nombre });
    } else {
      setCurrentTipo(null);
      setFormData({ nombre: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setLoading(true);
    try {
      const data = { nombre: formData.nombre.trim() };
      if (currentTipo) {
        await api.put(`/tipos-viaje/${currentTipo.id}`, data);
        toast.success('Tipo de viaje actualizado');
      } else {
        await api.post('/tipos-viaje', data);
        toast.success('Tipo de viaje creado');
      }
      await loadTipos();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/tipos-viaje/${currentTipo.id}`);
      toast.success('Tipo de viaje eliminado');
      await loadTipos();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tipos-viaje-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Tipos de Viaje</h1>
          <p className="text-gray-600 mt-1">Mantenedor de tipos de viaje (Automóvil, Van, Minibús, etc.)</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-tipo-viaje-button">
          <Plus size={18} className="mr-2" />
          Nuevo Tipo
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tipos-viaje-input"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredTipos.length} resultado{filteredTipos.length !== 1 ? 's' : ''} encontrado{filteredTipos.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo de Viaje</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTipos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No se encontraron tipos de viaje' : 'No hay tipos de viaje registrados'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTipos.map((tipo) => (
                  <TableRow key={tipo.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Bus size={16} className="text-primary" />
                        {tipo.nombre}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(tipo)}
                          data-testid={`edit-tipo-viaje-${tipo.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentTipo(tipo);
                            setShowDeleteDialog(true);
                          }}
                          data-testid={`delete-tipo-viaje-${tipo.id}`}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="tipo-viaje-dialog">
          <DialogHeader>
            <DialogTitle>
              {currentTipo ? 'Editar Tipo de Viaje' : 'Nuevo Tipo de Viaje'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ nombre: e.target.value })}
                required
                placeholder="Ej: Automóvil, Van, Minibús, Furgón..."
                data-testid="nombre-tipo-viaje-input"
                autoFocus
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-tipo-viaje-button">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar el tipo de viaje <strong>{currentTipo?.nombre}</strong>?
              Los viajes ya creados con este tipo conservarán el nombre como texto, pero ya no estará disponible para nuevos viajes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
